import * as React from 'react';
import { compose, lifecycle, setDisplayName, withState, withHandlers, withProps, branch, renderNothing } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { loginRequest } from 'actions/shellActions';
import { getLoginPending, getLoginError, getLanguage } from 'selectors/shellSelectors';
import { FadeCollapse, Fade } from 'ui/transitions';
import { ErrorMessage } from 'ui/shared/ErrorMessage';
import { Lock, User } from 'ui/shapes';
import { Loading, Button, Input } from 'ui/shared';
import { loginToServerConfig } from 'logic/externalServices/serverConfig';

import './LoginForm.css';

const titleSrc = require('assets/images/oem/TitleLarge.png');

type Props = RecomposeProps & StateProps & DispProps & TranslateProps & OemProps & OwnProps;

export const RawLoginForm = ({
    username,
    password,
    handleSubmit,
    handleClickServerConfig,
    handleChangeUsername,
    handleChangePassword,
    loading,
    error,
    t,
    styles,
}: Props) =>
    <form
        id="login-form"
        onSubmit={ handleSubmit }
        style={ styles.container }
    >
        <img src={ titleSrc }/>
        <div style={ styles.body }>
            <div className="field">
                <label htmlFor="login-username">
                    { t('2970') }
                </label>
                <div
                    style={ styles.field }
                >
                    <User width="10px" height="10px" style={ styles.icon }/>
                    <Input
                        id="login-username"
                        value={ username }
                        maxLength={ 32 }
                        onChange={ handleChangeUsername }
                        autoFocus={ true }
                    />
                </div>
            </div>

            <div className="field">
                <label htmlFor="login-password">
                    { t('997') }
                </label>
                <div
                    style={ styles.field }
                >
                    <Lock width="10px" height="10px" style={ styles.icon }/>
                    <Input
                        id="login-password"
                        type="password"
                        value={ password }
                        maxLength={ 64 }
                        onChange={ handleChangePassword }
                    />
                </div>
            </div>

            <FadeCollapse visible={ !!error } height="3em" duration={ 200 } unmountOnExit={ true }>
                <ErrorMessage
                    text={ error && error.message }
                />
            </FadeCollapse>

            <label>
                <span>{ t('WC_LOGIN_CHOOSE_APP') }</span>
                <Fade visible={ loading } duration={ 200 }>
                    <Loading size="small"/>
                </Fade>
            </label>
            <span className="button-bar">
                <Button
                    id="login-webclient"
                    type="submit"
                    textKey="6.5.0.25737.94"
                />
                <Button
                    id="login-serverconfig"
                    type="button"
                    textKey="203"
                    onClick={ handleClickServerConfig }
                />
            </span>
            <div style={ styles.resetFloat }/>
        </div>
    </form>
;

export type OwnProps = {
    visible: boolean;
};
type RecomposeProps = {
    username: string;
    password: string;
    handleChangeUsername: React.ChangeEventHandler<HTMLInputElement>;
    handleChangePassword: React.ChangeEventHandler<HTMLInputElement>;
    handleClickServerConfig: React.MouseEventHandler<HTMLButtonElement>;
    handleSubmit: React.FormEventHandler<HTMLFormElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setUsername: (u: string) => void;
    _setPassword: (p: string) => void;
};
type StateProps = {
    error: Error | null;
    loading: boolean;
    langCode: string;
};
type DispProps = {
    loginRequest: typeof loginRequest;
};

const mapStateToProps = (state: State): StateProps => ({
    loading: getLoginPending(state),
    error: getLoginError(state),
    langCode: getLanguage(state),
});

const mapDispatchToProps = ({
    loginRequest,
});

export const LoginForm = compose<Props, OwnProps>(
    setDisplayName('LoginForm'),
    skin,
    translate(),
    withState('username', '_setUsername', ''),
    withState('password', '_setPassword', ''),
    connect(mapStateToProps, mapDispatchToProps),
    withHandlers<StateProps & DispProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeUsername: ({ _setUsername }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setUsername(e.target.value);
        },
        handleChangePassword: ({ _setPassword }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setPassword(e.target.value);
        },
        handleClickServerConfig: ({ username, password, langCode }) => (e: React.MouseEvent<HTMLButtonElement>) => {
            loginToServerConfig(username, password, langCode);
        },
        handleSubmit: ({ username, password, loginRequest }) => (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            loginRequest({ username, password });
            return false;
        },
    }),
    withProps(({ styles }: OemProps) => ({
        styles: {
            ...styles,
            container: {
                ...styles.Workspace,
                ...styles.Bordered,
                maxWidth: '352px', // because the title image is 350px; +2 x 1px for left & right border
            },
            body: {
                padding: '2em 2em 1.5em 2em',
            },
            field: {
                ...styles.Input,
                padding: '6px 10px',
            },
            icon: {
                fill: styles.Input.color,
            },
            resetFloat: {
                clear: 'both',
                float: 'none',
            },
        },
    })),
    lifecycle({
        componentDidUpdate(oldProps: _RecomposeProps & OwnProps) {
            // Reset the username and password whenever the LoginForm visibility changes
            if (oldProps.visible !== this.props.visible) {
                this.props._setUsername('');
                this.props._setPassword('');
            }
        },
    }),
    branch<Props>(({ visible }) => !visible, renderNothing),
)(RawLoginForm);



// WEBPACK FOOTER //
// ./src/ui/login/LoginForm.tsx